/* ──────────────────────────────────────────────────────────────
   src/pages/reports/asignaturas/AsignaturasDemandaPage.tsx
   Informe: tendencia de demanda por asignatura
   ────────────────────────────────────────────────────────────── */

import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

import { useAsignaturasDemandaReport } from '../../../hooks/useReportJob';
import type { AsignaturaDemandaRow } from '../../../api/reports';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

export default function AsignaturasDemandaPage() {
  const { t } = useTranslation<'translation'>();
  const monday = dayjs().startOf('isoWeek');
  const sunday = monday.add(6, 'day');

  const [desde, setDesde] = useState(monday.format('YYYY-MM-DD'));
  const [hasta, setHasta] = useState(sunday.format('YYYY-MM-DD'));
  const [periodo, setPeriodo] = useState<'semanal' | 'mensual'>('semanal');
  const [search, setSearch] = useState('');

  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useAsignaturasDemandaReport();
  const { data: status } = useStatus(jobId);

  const pendiente = !!status && !status.done && !status.failed;
  const rows: AsignaturaDemandaRow[] = status?.result?.rows ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => r.asignatura.toLowerCase().includes(q));
  }, [rows, search]);

  const handleGenerate = async () => {
    const { jobId } = await run({ desde, hasta, periodo });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      await downloadReportFile(status.result.url);
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('asigDemanda.titulo')}</Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          type="date"
          label={t('asigDemanda.filtros.desde')}
          size="small"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          label={t('asigDemanda.filtros.hasta')}
          size="small"
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('asigDemanda.filtros.periodo')}</InputLabel>
          <Select
            label={t('asigDemanda.filtros.periodo')}
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value as 'semanal' | 'mensual')}
          >
            <MenuItem value="semanal">{t('asigDemanda.filtros.semanal')}</MenuItem>
            <MenuItem value="mensual">{t('asigDemanda.filtros.mensual')}</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleGenerate}>
          {t('asigDemanda.filtros.generar')}
        </Button>

        {rows.length > 0 && (
          <TextField
            placeholder={t<string>('asigDemanda.filtros.buscar')}
            size="small"
            sx={{ ml: 'auto', maxWidth: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Stack>

      {pendiente && status && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t('asigDemanda.estado.generando', { progress: status.progress })}
          </Typography>
        </Stack>
      )}

      {status?.done && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Alert severity="success">{t('asigDemanda.estado.exito', { count: rows.length })}</Alert>
          {status.result?.url && (
            <Button size="small" onClick={handleDownload}>
              {t('asigDemanda.estado.descargar')}
            </Button>
          )}
        </Stack>
      )}
      {status?.failed && <Alert severity="error">{t('asigDemanda.estado.error')}</Alert>}

      {status?.done && (
        <Paper>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t('asigDemanda.tabla.asignatura')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t('asigDemanda.tabla.periodo')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {t('asigDemanda.tabla.reservas')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {t('asigDemanda.tabla.horas')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row, idx) => (
                  <TableRow key={`${row.asignaturaId}-${row.periodKey}-${idx}`} hover>
                    <TableCell>{row.asignatura}</TableCell>
                    <TableCell>{row.periodLabel}</TableCell>
                    <TableCell align="right">{row.numReservas}</TableCell>
                    <TableCell align="right">{row.horas}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Stack>
  );
}
